/** Allow 'any' across this file to make conversions to and from JSON easier to work with.
 * Actual JSON structure shape is nevertheless asserted. */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { isArray, isObject, isString, get, trim, isEmpty, groupBy, maxBy } from "lodash-es";
import hash from 'object-hash';

import { assertExpectedProps, PropType } from "../../util/expected-prop";
import { naturalSort } from "../../util/sort";
import { CodingScheme, GlobalContouringRoi, ContouringCustomizationEntities, ContouringCustomizationEntitiesForExport, ContouringCustomizationOutput, ContouringCustomizationOutputEntities, PhysicalProperty, PHYSICAL_PROPERTY_REL_ELEC_DENSITY, PHYSICAL_PROPERTY_REL_MASS_DENSITY, ContouringRoi, SUPPORTED_PHYSICAL_PROPERTIES } from "./contouring-types";
import { Model, CustomizationBase, OutputMetadataItem, AeTitleRule, DicomRule, DicomAttributeRule, isAeTitleRule, isDicomRule, METADATA_FILENAME_ATTRIBUTE, ModelType } from "../global-types/customization-types";
import { generateNewId, createNewModelCustomizationBase, UI_ID_ATTRIBUTE } from "../global-types/customization-helpers";
import { _convertOutputMetadataViewModelToJson, convertCustomizationJsonObjectToViewModels, convertCustomizationViewModelsToJson, convertSingleCustomizationJsonEntryToViewModelObjects, CustomizationBaseExtractResult, extractOutputMetadata, ModelTriggersByCustomizationNames } from "../global-types/view-model-conversion-helpers";

/** List of coding scheme field keys. */
const CODING_SCHEME_FIELD_KEYS = [
    'codingSchemeCodeValue',
    'codingSchemeCodeMeaning',
    'codingSchemeDesignator',
    'codingSchemeVersion',
    'codingSchemeMappingResource',
    'codingSchemeContextGroupVersion',
    'codingSchemeContextIdentifier',
    'codingSchemeContextUid',
    'codingSchemeMappingResourceUid',
    'codingSchemeMappingResourceName',
] as const;

/** Returns true if the given object has at least one non-empty coding scheme field value. */
export const hasCodingScheme = (obj: Partial<CodingScheme>): boolean => {
    return CODING_SCHEME_FIELD_KEYS.some(key => {
        const value = obj[key];
        return typeof value === 'string' && value.trim() !== '';
    });
};

export const createNewContouringCustomizationOutput = (metadataIds: string[], roiIds: string[], customizationBaseId: string, filename?: string, id?: string, isModified?: boolean): ContouringCustomizationOutput => ({
    id: id || generateNewId(),
    metadata: metadataIds,
    rois: roiIds,
    modelCustomizationBaseId: customizationBaseId,
    filename: filename || '',
    isModified: isModified || false,
});

export const createNewContouringRoi = (operation: string, name: string, color: [number, number, number],
    interpretedType: string, physicalPropertyAttribute: PhysicalProperty, physicalPropertyValue: string,
    codingScheme: CodingScheme, isIncluded: boolean, canBeDeleted: boolean, modelCustomizationOutputId?: string,
    id?: string, isModified?: boolean, scrollToView?: boolean): ContouringRoi => ({
        id: id || generateNewId(),
        operation: operation,
        name: name,
        color: color,
        interpretedType: interpretedType,
        physicalPropertyAttribute: physicalPropertyAttribute,
        physicalPropertyValue: physicalPropertyValue,
        ...codingScheme,
        isIncluded: isIncluded,
        isBuiltInRoi: canBeDeleted === false,
        isModified: isModified || false,
        customizationOutputId: modelCustomizationOutputId,
        scrollToView: scrollToView || false,
    });

const createNewCodingScheme = (codeValue: string, codeMeaning: string, codingSchemeDesignator: string, codingSchemeVersion: string,
    mappingResource: string, contextGroupVersion: string, contextIdentifier: string, contextUid: string, mappingResourceUid: string,
    mappingResourceName: string): CodingScheme => ({
        codingSchemeCodeValue: codeValue,
        codingSchemeCodeMeaning: codeMeaning,
        codingSchemeDesignator: codingSchemeDesignator,
        codingSchemeVersion: codingSchemeVersion,
        codingSchemeMappingResource: mappingResource,
        codingSchemeContextGroupVersion: contextGroupVersion,
        codingSchemeContextIdentifier: contextIdentifier,
        codingSchemeContextUid: contextUid,
        codingSchemeMappingResourceUid: mappingResourceUid,
        codingSchemeMappingResourceName: mappingResourceName,
    });

/**
 * Filters coding scheme changes based on the skipNonEmpty flag and value comparison.
 * Only includes changes where the new value is different from the existing value.
 * When skipNonEmpty is true, additionally only returns changes for fields that are currently empty in the target ROI.
 * @param codingSchemeChanges The coding scheme changes to apply.
 * @param targetRoi The target ROI to check for existing values.
 * @param skipNonEmpty If true, only include changes for fields that are empty in the target ROI.
 */
export const filterCodingSchemeChanges = (
    codingSchemeChanges: Partial<CodingScheme>,
    targetRoi: CodingScheme,
    skipNonEmpty: boolean
): Partial<CodingScheme> => {
    const filteredChanges: Partial<CodingScheme> = {};
    for (const [key, value] of Object.entries(codingSchemeChanges)) {
        const currentValue = targetRoi[key as keyof CodingScheme];
        const trimmedCurrentValue = isString(currentValue) ? trim(currentValue) : currentValue;
        const trimmedNewValue = isString(value) ? trim(value) : value;
        const isEmpty = trimmedCurrentValue === undefined || trimmedCurrentValue === null || trimmedCurrentValue === '';

        // Skip if new value is the same as the current value
        const normalizedCurrent = isEmpty ? '' : trimmedCurrentValue;
        const normalizedNew = trimmedNewValue === undefined || trimmedNewValue === null ? '' : trimmedNewValue;
        if (normalizedCurrent === normalizedNew) {
            continue;
        }

        // Skip non-empty fields if skipNonEmpty is enabled
        if (skipNonEmpty && !isEmpty) {
            continue;
        }

        (filteredChanges as Record<string, unknown>)[key] = value;
    }
    return filteredChanges;
};

/**
 * Generates a new GlobalRoiCustomization object.
 * @param coveredRois array of matching RoiCustomizations that this global roi covers
 * @param excludedRois array of RoiCustomizations that have the same operation as this global roi but do not otherwise match with this one
 * @param mutateRois If true, set the input covered rois' globalRoiId property to match this new object and
 * set the input excluded rois' globalRoiId property to undefined.
 * If false or undefined, don't mutate the input ROI objects. If you call this function from redux store you probably
 * want to leave this as false/undefined and do any prop changing to the original roi objects manually.
 */
export const createGlobalRoiCustomization = (coveredRois: ContouringRoi[], excludedRois: ContouringRoi[], mutateRois?: boolean): GlobalContouringRoi => {
    const globalRoi: GlobalContouringRoi = {
        id: generateNewId(),
        operation: coveredRois[0].operation,
        name: coveredRois[0].name,
        isIncluded: coveredRois[0].isIncluded,
        color: coveredRois[0].color,
        interpretedType: coveredRois[0].interpretedType,
        physicalPropertyAttribute: coveredRois[0].physicalPropertyAttribute,
        physicalPropertyValue: coveredRois[0].physicalPropertyValue,
        ...getCodingScheme(coveredRois[0]),
        isModified: false,
        isBuiltInRoi: coveredRois.some(r => r.isBuiltInRoi === true),
        coveredRois: coveredRois.map(r => r.id),
        excludedRois: excludedRois.map(r => r.id)
    };
    if (mutateRois) { 
        coveredRois.forEach(cr => cr.globalRoiId = globalRoi.id);
        excludedRois.forEach(er => er.globalRoiId = undefined);
     }
    return globalRoi;
};

/**
 * Groups ROIs into global ROI groupings by operation, finding the majority
 * group of identical ROIs per operation. Does NOT mutate input ROI objects.
 */
export const calculateGlobalRoiGroupings = (rois: ContouringRoi[]): {
    roiUpdates: { id: string, changes: { globalRoiId: string | undefined } }[],
    globalRois: GlobalContouringRoi[]
} => {
    const globalRois: GlobalContouringRoi[] = [];
    const roiUpdates: { id: string, changes: { globalRoiId: string | undefined } }[] = [];
    const roisGroupedByOperation = groupBy(rois, roi => roi.operation);

    for (const roiGroup of Object.values(roisGroupedByOperation)) {
        const hashedCustomizations: { [hash: string]: ContouringRoi[] } = {};
        for (const roiCustomization of roiGroup) {
            const hashedRoi = generateRoiCustomizationHash(roiCustomization);
            hashedCustomizations[hashedRoi] = hashedCustomizations[hashedRoi] || [];
            hashedCustomizations[hashedRoi].push(roiCustomization);
        }
        const largestGroupHash = maxBy(Object.keys(hashedCustomizations), hash => hashedCustomizations[hash].length);
        if (largestGroupHash === undefined) { throw new Error('Could not find hash for largest group for roi'); }
        const largestGroupRois = hashedCustomizations[largestGroupHash];
        const largestGroupRoiIds = largestGroupRois.map(r => r.id);
        const excludedRois = roiGroup.filter(r => !largestGroupRoiIds.includes(r.id));
        const globalRoi = createGlobalRoiCustomization(largestGroupRois, excludedRois);
        globalRois.push(globalRoi);

        largestGroupRois.forEach(roi => roiUpdates.push({ id: roi.id, changes: { globalRoiId: globalRoi.id } }));
        excludedRois.forEach(roi => roiUpdates.push({ id: roi.id, changes: { globalRoiId: undefined } }));
    }

    return { roiUpdates, globalRois };
};

/** Extracts a (new) coding scheme (FmaId) object out of a ROI or a Global ROI customization object. */
export const getCodingScheme = (roi: ContouringRoi | GlobalContouringRoi): CodingScheme => {
    return {
        codingSchemeCodeValue: roi.codingSchemeCodeValue,
        codingSchemeCodeMeaning: roi.codingSchemeCodeMeaning,
        codingSchemeDesignator: roi.codingSchemeDesignator,
        codingSchemeVersion: roi.codingSchemeVersion,
        codingSchemeMappingResource: roi.codingSchemeMappingResource,
        codingSchemeContextGroupVersion: roi.codingSchemeContextGroupVersion,
        codingSchemeContextIdentifier: roi.codingSchemeContextIdentifier,
        codingSchemeContextUid: roi.codingSchemeContextUid,
        codingSchemeMappingResourceUid: roi.codingSchemeMappingResourceUid,
        codingSchemeMappingResourceName: roi.codingSchemeMappingResourceName,
    }
}

/**
 * Cleans a JSON coding scheme (FmaId) object by removing keys with empty or whitespace-only string values.
 * @param codingScheme The coding scheme (FmaId) JSON object to clean.
 * @returns The cleaned FmaId JSON object or null if all fields are empty or if the input is null.
 */
export const cleanCodingScheme = (codingScheme: any | null): any | null => {
    if (codingScheme === null) {
        return null;
    }

    const cleanedCodingScheme: any = {};
    for (const key in codingScheme) {
        if (key in codingScheme) {
            const value = codingScheme[key];
            if (typeof value === 'string' && trim(value) !== '') {
                cleanedCodingScheme[key] = value;
            }
        }
    }

    return isEmpty(cleanedCodingScheme) ? null : cleanedCodingScheme;
};

/** Returns a default (empty) coding scheme (FmaId) object. */
export const getDefaultCodingScheme = (): CodingScheme => ({});

/** Convert contouring customization JSON from config API into flattened internal view model representation.
 * 
 * Regarding sorting: if any sorting is done, it's generally done to try to ensure order by original
 * name using naturalSort. Dynamic sorting during user operation is generally not wanted as it could
 * erratically shift the position of the element on the page. Set the 'noSorting' prop to 'true' if sorting
 * is not wanted (e.g. for roundtrip unit tests).
 */
export const convertContouringJsonObjectToViewModels = (jsonObject: any, noSorting: boolean = false): ContouringCustomizationEntities => {

    const results = convertCustomizationJsonObjectToViewModels(jsonObject, ModelType.Contouring, noSorting);

    // return converted results with following notes:
    // - ignore any fields related to other algorithms (although if there were anything like that present then 
    //   the earlier conversion function should have already thrown)
    return {
        models: results.models || [],
        customizationBases: results.customizationBases || [],
        contouringOutputs: results.contouringOutputs || [],
        outputMetadata: results.outputMetadata || [],
        contouringRois: results.contouringRois || [],
        aeTitleRules: results.aeTitleRules || [],
        dicomRules: results.dicomRules || [],
        dicomAttributeRules: results.dicomAttributeRules || [],
        contouringGlobalRois: results.contouringGlobalRois || [],
    }
}

/** Convert model customization DTO/JSON containing just ONE model customization from segmentation backend 
 * API into flattened internal view model representation. Returns matching view model objects but does NOT
 * generate matching global ROI objects.
 * 
 * Regarding sorting: if any sorting is done, it's generally done to try to ensure order by original
 * name using naturalSort. Dynamic sorting during user operation is generally not wanted as it could
 * erratically shift the position of the element on the page. Set the 'noSorting' prop to 'true' if sorting
 * is not wanted (e.g. for roundtrip unit tests).
 */
export const convertSingleModelJsonToContouringOutput = (jsonObject: any, noSorting: boolean = false): ContouringCustomizationOutputEntities => {
    if (!jsonObject || !isObject(jsonObject)) {
        throw new Error('Invalid JSON object provided for contouring customizations conversion.');
    }

    const modelCollection: Model[] = [];
    const customizationBaseCollection: CustomizationBase[] = [];
    const outputCollection: ContouringCustomizationOutput[] = [];
    const metadataCollection: OutputMetadataItem[] = [];
    const roiCustomizationCollection: ContouringRoi[] = [];
    const aeTitleRuleCollection: AeTitleRule[] = [];
    const dicomRuleCollection: DicomRule[] = [];
    const dicomAttributeRuleCollection: DicomAttributeRule[] = [];

    const assertMessage = 'Unexpected contouring model/structure customization format.';

    convertSingleCustomizationJsonEntryToViewModelObjects(
        jsonObject,

        modelCollection,
        customizationBaseCollection,
        metadataCollection,
        aeTitleRuleCollection,
        dicomRuleCollection,
        dicomAttributeRuleCollection,

        {
            contouringOutputCollection: outputCollection,
            contouringRoiCollection: roiCustomizationCollection,
        },

        assertMessage,
        ModelType.Contouring,
        noSorting
    );

    return {
        customizationOutputs: outputCollection,
        modelCustomizationsMetadata: metadataCollection,
        roiCustomizations: roiCustomizationCollection,
    }
}



/**
 * Helper function for converting a customization base and all its outputs and other child objects into contouring customization entities.
 * You shouldn't need to call this function manually.
 */
export const _convertContouringCustomizationOutputJsonToViewModel = (
    modelCustomizationBaseResult: CustomizationBaseExtractResult,
    modelId: string,
    contouringOutputCollection: ContouringCustomizationOutput[],
    outputMetadataCollection: OutputMetadataItem[],
    contouringRoiCollection: ContouringRoi[],
    modelTriggersByCustomizationNames: ModelTriggersByCustomizationNames,
    assertMessage: string,
    noSorting: boolean,
): CustomizationBase => {
    const { customizationId, customizationName, customizationDescription, dtoOutputs } = modelCustomizationBaseResult;

    // each 'file' will become its own customization output object
    const outputs: ContouringCustomizationOutput[] = dtoOutputs.map((customizationOutputEntry: any) => {

        // assert that output entries look correct
        assertExpectedProps(customizationOutputEntry, [
            { propName: 'metadata', propType: PropType.Array },
            { propName: 'rois', propType: PropType.Array },
        ], assertMessage);

        // extract metadata
        const metadata = extractOutputMetadata(customizationOutputEntry, assertMessage);

        // extract rois & fma ids
        const rois: ContouringRoi[] = customizationOutputEntry['rois'].map((roiEntry: any) => {
            // assert that metadata entries look correct
            assertExpectedProps(roiEntry, [
                { propName: 'operation', propType: PropType.String },
                { propName: 'name', propType: PropType.String },
                { propName: 'color', propType: PropType.RgbArray },
                { propName: 'interpreted_type', propType: PropType.String },
                { propName: 'can_be_deleted', propType: PropType.Boolean },
                { propName: 'included', propType: PropType.Boolean },
            ], assertMessage);

            const codingSchemeEntry = roiEntry['fma_id'];
            const parsedCodingScheme = codingSchemeEntry ? createNewCodingScheme(
                codingSchemeEntry['code_value'],
                codingSchemeEntry['code_meaning'],
                codingSchemeEntry['coding_scheme_designator'],
                codingSchemeEntry['coding_scheme_version'],
                codingSchemeEntry['mapping_resource'],
                codingSchemeEntry['context_group_version'],
                codingSchemeEntry['context_identifier'],
                codingSchemeEntry['context_uid'],
                codingSchemeEntry['mapping_resource_uid'],
                codingSchemeEntry['mapping_resource_name']
            ) : {};

            let physicalPropertyAttribute = PhysicalProperty.NotSet;
            let physicalPropertyValue = '';
            const physicalPropertiesEntry = roiEntry['physical_properties'];
            if (physicalPropertiesEntry && isArray(physicalPropertiesEntry)) {
                const matchingEntry = physicalPropertiesEntry.find(e => isString(e['physical_property']) && SUPPORTED_PHYSICAL_PROPERTIES.includes(e['physical_property']));
                if (matchingEntry) {
                    physicalPropertyAttribute = matchingEntry['physical_property'] === PHYSICAL_PROPERTY_REL_ELEC_DENSITY ? PhysicalProperty.RelElecDensity : PhysicalProperty.RelMassDensity;
                    physicalPropertyValue = matchingEntry['value'].toString() || '';
                }
            }

            return createNewContouringRoi(roiEntry['operation'],
                roiEntry['name'],
                roiEntry['color'],
                roiEntry['interpreted_type'],
                physicalPropertyAttribute,
                physicalPropertyValue,
                parsedCodingScheme,
                roiEntry['included'],
                roiEntry['can_be_deleted']);
        });

        // collect ids. ensure roi id order within models
        const metadataIds = metadata.map(m => m.id);
        const roiIds = noSorting ? rois.map(r => r.id) : naturalSort(rois, 'name').map(r => r.id);
        const filename = metadata.find(m => m.attribute === METADATA_FILENAME_ATTRIBUTE)?.value;

        const output = createNewContouringCustomizationOutput(metadataIds, roiIds, customizationId, filename);

        // update parent ids to child objects
        metadata.forEach(m => m.modelCustomizationOutputId = output.id);
        rois.forEach(r => r.customizationOutputId = output.id);

        // add newly created objects into collections that will eventually be put into redux store
        outputMetadataCollection.push(...metadata);
        contouringRoiCollection.push(...rois);

        return output;
    });

    const outputIds = outputs.map(o => o.id);
    const matchingTriggers = modelTriggersByCustomizationNames.get(customizationName) ?? [];
    const aeTitleRules = matchingTriggers.filter(isAeTitleRule);
    const dicomRules = matchingTriggers.filter(isDicomRule);
    const aeTitleRuleIds = aeTitleRules.map(t => t.id);
    const dicomRuleIds = dicomRules.map(t => t.id);
    const customization = createNewModelCustomizationBase(customizationName, customizationDescription, outputIds, aeTitleRuleIds, dicomRuleIds, modelId, customizationId);

    // update matching customization IDs now that we have a customization object
    matchingTriggers.forEach(t => t.modelCustomizationBaseId = customization.id);

    // add newly created objects into collections that will eventually be put into redux store
    contouringOutputCollection.push(...outputs);

    return customization;
}


/** Convert contouring customization view models to JSON presentation..
 * @param contouringEntities The contouring customization view model entities to convert to JSON.
 * @param noUiId Internal IDs for the view models needed for UI functionality will not be included in the export if true, and will be included
 * if set to false. Defaults to false. Including internal IDs is needed when sending data to backend to be able to cross-reference received
 * errors to their matching UI view models, but these IDs should be omitted when this JSON will be user-facing (e.g. when exported directly
 * as a downloaded file) or when running certain unit tests (e.g. round-trip tests will break if extra UI ID fields are present).
 * @returns An array of model JSON objects, ready to be sent to backend.
 */
export const convertContouringViewModelsToJson = (contouringEntities: Partial<ContouringCustomizationEntitiesForExport>, noUiId: boolean = false): any => {

    const jsonModels = convertCustomizationViewModelsToJson(contouringEntities, ModelType.Contouring, noUiId);

    return jsonModels;
}


/**
 * Convert internal view model representation of contouring output objects and related children
 * into a model customization JSON hierarchy. You shouldn't need to call this manually.
 * @param jsonCustomization Result JSON will be embedded into this JSON.
 * @param output Contouring output view model to convert to JSON.
 * @param outputMetadata Collection of output metadata related to this customization set.
 * @param contouringRois Collection of ROI customizations related to this customization set.
 * @param errorMessage Error message to throw on failure.
 * @param noUiId Marks if internal UI IDs should be included in converted JSON.
 */
export const _convertContouringCustomizationOutputToJson = (
    jsonCustomization: any,
    output: ContouringCustomizationOutput,
    outputMetadata: OutputMetadataItem[],
    contouringRois: ContouringRoi[],
    errorMessage: string,
    noUiId: boolean = false) => {

    if (output === undefined) { throw new Error(errorMessage); }
    const jsonOutput: any = {
        'metadata': [],
        'rois': [],
    };
    if (!noUiId) { jsonOutput[UI_ID_ATTRIBUTE] = output.id }

    for (const metadataId of output.metadata) {
        const metadata = outputMetadata.find(m => m.id === metadataId);
        if (metadata === undefined) { throw new Error(errorMessage); }
        _convertOutputMetadataViewModelToJson(jsonOutput, metadata, noUiId);
    }

    for (const roiId of output.rois) {
        const roi = contouringRois.find(r => r.id === roiId);
        if (roi === undefined) { throw new Error(errorMessage); }

        const codingSchemeOutput = hasCodingScheme(roi) ? {
            'code_value': roi.codingSchemeCodeValue?.trim(),
            'code_meaning': roi.codingSchemeCodeMeaning?.trim(),
            'coding_scheme_designator': roi.codingSchemeDesignator?.trim(),
            'coding_scheme_version': roi.codingSchemeVersion?.trim(),
            'mapping_resource': roi.codingSchemeMappingResource?.trim(),
            'context_group_version': roi.codingSchemeContextGroupVersion?.trim(),
            'context_identifier': roi.codingSchemeContextIdentifier?.trim(),
            'context_uid': roi.codingSchemeContextUid?.trim(),
            'mapping_resource_uid': roi.codingSchemeMappingResourceUid?.trim(),
            'mapping_resource_name': roi.codingSchemeMappingResourceName?.trim(),
        } : null;

        const cleanedCodingSchemeOutput = cleanCodingScheme(codingSchemeOutput);

        const physicalProperties = [];
        if (roi.physicalPropertyAttribute !== PhysicalProperty.NotSet && roi.physicalPropertyValue) {
            const physicalProperty = {
                'physical_property': roi.physicalPropertyAttribute === PhysicalProperty.RelElecDensity ? PHYSICAL_PROPERTY_REL_ELEC_DENSITY : PHYSICAL_PROPERTY_REL_MASS_DENSITY,
                'value': parseFloat(roi.physicalPropertyValue.trim()),
            };
            if (!isNaN(physicalProperty.value)) {
                physicalProperties.push(physicalProperty);
            }
        }

        const jsonRoi: any = {
            'included': roi.isIncluded,
            'name': roi.name.trim(),
            'color': roi.color,
            'interpreted_type': roi.interpretedType,
            'can_be_deleted': !roi.isBuiltInRoi,
            'operation': roi.operation.trim(),
            'fma_id': cleanedCodingSchemeOutput,
            'physical_properties': physicalProperties,
        };
        if (!noUiId) { jsonRoi[UI_ID_ATTRIBUTE] = roi.id }
        jsonOutput['rois'].push(jsonRoi);
    }

    jsonCustomization['files'].push(jsonOutput);
}

export const generateRoiCustomizationHash = (roi: ContouringRoi | GlobalContouringRoi): string => {
    // separate fma id value props from other keys
    const codingSchemeValueKeys = ['codingSchemeCodeValue', 'codingSchemeCodeMeaning', 'codingSchemeDesignator', 'codingSchemeVersion', 'codingSchemeMappingResource',
        'codingSchemeContextGroupVersion', 'codingSchemeContextIdentifier', 'codingSchemeContextUid', 'codingSchemeMappingResourceUid', 'codingSchemeMappingResourceName'];

    // only these keys are used for checking similarity
    const supportedKeys = ['operation', 'name', 'color', 'interpretedType', 'physicalPropertyAttribute', 'physicalPropertyValue', ...codingSchemeValueKeys];

    // ignore all keys that return 'true' in the excludeKeys function below, i.e. exclude
    // 1. anything that's not in supportedKeys,
    // 2. codingSchemeValueKeys if the actual matching values are undefined (this improves optional property support between roi and globalroi objects)
    return hash(roi, { excludeKeys: (key) => !supportedKeys.includes(key) || (codingSchemeValueKeys.includes(key) && get(roi, key, undefined)) === undefined });
}

/**
 * Returns true if given roi customization object essentially matches given global roi customization object, such that
 * the global roi customization covers this roi customization.
 * @param globalRoiUpdates An optional update object for global roi customization object can be given. Any values in this object
 * are used instead of the matching props in globalRoi. This is useful for testing matching during data updates where final updates
 * haven't been applied to the globalRoi object yet.
 */
export const roiCustomizationMatchesGlobalRoi = (roi: ContouringRoi, globalRoi: GlobalContouringRoi, globalRoiUpdates?: Partial<GlobalContouringRoi>): boolean => {
    if (globalRoiUpdates === undefined) {
        return generateRoiCustomizationHash(roi) === generateRoiCustomizationHash(globalRoi);
    } else {
        return generateRoiCustomizationHash(roi) === generateRoiCustomizationHash(Object.assign({}, globalRoi, globalRoiUpdates));
    }
}

export const duplicateRoiCustomization = (roi: ContouringRoi, outputId?: string): ContouringRoi => {
    return {
        id: generateNewId(),
        operation: roi.operation,
        name: roi.name,
        color: roi.color,
        interpretedType: roi.interpretedType,
        physicalPropertyAttribute: roi.physicalPropertyAttribute,
        physicalPropertyValue: roi.physicalPropertyValue,
        ...getCodingScheme(roi),
        isIncluded: roi.isIncluded,
        isBuiltInRoi: roi.isBuiltInRoi,
        isModified: true,
        customizationOutputId: outputId,
        scrollToView: false,
        globalRoiId: roi.globalRoiId,
    };
}

/**
 * Extracts the common coding scheme attributes from the given ROI customization.
 * Note: Only the common coding scheme attributes are returned.
 * @param roiCustomization The ROI customization object.
 * @returns The common coding scheme attributes, with some fields potentially undefined.
 */
export const extractSharedCodingSchemeAttributes = (roiCustomization: ContouringRoi | GlobalContouringRoi): Partial<CodingScheme> => {
    const extracted: Partial<CodingScheme> = {
        codingSchemeDesignator: roiCustomization.codingSchemeDesignator,
        codingSchemeVersion: roiCustomization.codingSchemeVersion,
        codingSchemeMappingResource: roiCustomization.codingSchemeMappingResource,
        codingSchemeContextGroupVersion: roiCustomization.codingSchemeContextGroupVersion,
        codingSchemeContextIdentifier: roiCustomization.codingSchemeContextIdentifier,
        codingSchemeContextUid: roiCustomization.codingSchemeContextUid,
        codingSchemeMappingResourceUid: roiCustomization.codingSchemeMappingResourceUid,
        codingSchemeMappingResourceName: roiCustomization.codingSchemeMappingResourceName,
    };

    return extracted;
};



export const readModelCustomizationJsonFile = (jsonFile: File): Promise<ContouringCustomizationEntities | null> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            try {
                const jsonObject = JSON.parse(reader.result as string);

                const modelCustomizationImport = convertContouringJsonObjectToViewModels(jsonObject);
                resolve(modelCustomizationImport);
            } catch (e) {
                reject(e);
            }
        };

        reader.onerror = (error) => {
            reject(error);
        };

        reader.readAsText(jsonFile);
    });
};
